import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes';
import { useCategories } from '../../../hooks/useCategories';
import { getCategoryIconType } from '../../../utils/categoryNav';
import { CategoryIcon } from './CategoryIcons';
import backgroundImage from '../../../assets/images/category.svg';

function ChevronLeft() {
  return (
    <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function resolveCategoryId(categories, classLabel) {
  const label = String(classLabel || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  if (!label) return null;

  const match = categories.find((category) => {
    const name = String(category?.name || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
    if (!name) return false;
    return name === label || name.includes(label) || label.includes(name);
  });

  return match?.id ?? null;
}

function CategoryCardSkeleton() {
  return (
    <div className="flex h-[151px] w-[145px] shrink-0 animate-pulse flex-col items-center justify-center rounded-lg bg-white/90">
      <div className="h-10 w-10 rounded-full bg-gray-200" />
      <div className="mt-3 h-4 w-16 rounded bg-gray-200" />
    </div>
  );
}

function CategoryCard({ subject, onClick }) {
  const subjectName = subject?.subject || 'Subject';
  const className = subject?.class || '';
  const iconUrl = subject?.icon_url || '';

  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative flex h-[151px] w-[145px] shrink-0 flex-col items-center justify-center rounded-lg bg-white px-2 shadow-none transition-[border-radius,box-shadow] duration-300 ease-out hover:z-10 hover:rounded-[30px] hover:shadow-[0_10px_20px_rgba(0,0,0,0.2)]"
    >
      {className ? (
        <span className="absolute right-3 top-3 rounded-full bg-[#F3EDFF] px-2 py-1 text-[10px] font-semibold leading-none text-[#9369CC]">
          {className}
        </span>
      ) : null}

      {iconUrl ? (
        <img
          src={iconUrl}
          alt={subjectName}
          className="h-14 w-14 object-contain"
          loading="lazy"
        />
      ) : (
        <CategoryIcon type={getCategoryIconType(subjectName)} />
      )}

      <span className="mt-1 text-center text-[18px] font-medium text-[#666666] transition-all duration-300 sm:text-[20px] group-hover:font-semibold">
        {subjectName}
      </span>
    </button>
  );
}

function CategorySection({ subjects = [], loading = false, error = null }) {
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const { categories } = useCategories();

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({ left: 0, behavior: 'auto' });
  }, [subjects]);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const amount = direction === 'left' ? -200 : 200;
    scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
  };

  const handleSubjectClick = (subject) => {
    const categoryId =
      subject.category_id || resolveCategoryId(categories, subject.class);

    if (!categoryId || !subject?.id) return;

    const params = new URLSearchParams({
      category_id: String(categoryId),
      subject_id: String(subject.id),
    });

    navigate(`${ROUTES.COURSES_BROWSE}?${params.toString()}`, {
      state: {
        categoryName: subject.class || 'Class',
        subjectName: subject.subject,
      },
    });
  };

  const renderSubjects = () => {
    if (loading && !subjects.length) {
      return (
        <>
          {[1, 2, 3, 4].map((item) => (
            <CategoryCardSkeleton key={item} />
          ))}
        </>
      );
    }

    if (error) {
      return <p className="w-full text-center text-sm text-red-300">{error}</p>;
    }

    if (!subjects.length) {
      return <p className="w-full text-center text-sm text-white/70">No subjects available right now.</p>;
    }

    return subjects.map((subject) => (
      <CategoryCard
        key={subject.id}
        subject={subject}
        onClick={() => handleSubjectClick(subject)}
      />
    ));
  };

  return (
    <div
      className="relative w-full min-h-[300px] overflow-hidden bg-cover bg-center bg-no-repeat px-4 py-10 sm:px-6 lg:min-h-[334px] lg:px-8 lg:py-16"
      style={{
        backgroundImage: `url(${backgroundImage})`,
      }}
    >
      <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between lg:gap-6 xl:gap-8">
        <div className="max-w-md shrink-0 text-white">
          <h2 className="text-3xl font-medium sm:text-4xl lg:text-[36px] xl:text-[36px]">
            Explore{' '}
            <span className="block mt-4 text-4xl font-medium lg:text-[40px] xl:text-[48px]">Our Category</span>
          </h2>

          <p className="mt-5 text-sm leading-relaxed text-[#cccccc] sm:text-base">
            From foundation to mastery, explore courses that fit every learner&apos;s journey.
          </p>
        </div>

        <div className="flex w-full min-w-0 flex-1 items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => scroll('left')}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white/90 transition sm:h-12 sm:w-12"
            aria-label="Previous categories"
          >
            <ChevronLeft />
          </button>

          <div
            ref={scrollRef}
            className="min-w-0 flex-1 overflow-x-auto scroll-smooth px-2 py-6 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <div className="flex w-max flex-nowrap items-center justify-start gap-4">
              {renderSubjects()}
            </div>
          </div>

          <button
            type="button"
            onClick={() => scroll('right')}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white/90 transition hover:bg-white/10 sm:h-12 sm:w-12"
            aria-label="Next categories"
          >
            <ChevronRight />
          </button>
        </div>
      </div>
    </div>
  );
}

export default CategorySection;
